import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ServiceLine } from '@ata-digital/client-tax-system-api-client';
import { PasswordlessAuthFlow } from '../components/auth/PasswordlessAuthFlow';
import { useEffect, useState } from 'react';
import { useAvailableClients } from '@/hooks/api/useClients';
import { useAuthState } from '@/hooks/auth/useAuth';
import { Box, CircularProgress } from '@mui/material';
import { createLogger } from '@/utils/logger';
import { clientTokenManager } from '@/lib/auth/tokenManager';

const log = createLogger('LoginPage');

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    // Clean up any stale client auth data from localStorage.
    // Client auth now uses sessionStorage (via clientTokenManager) to isolate sessions per tab.
    // Stale localStorage data with userType='client' can cause routing loops because:
    // - This route's beforeLoad would see the stale token and redirect to /
    // - But __root.tsx checks sessionStorage (no token) and redirects back to /login
    // See: CLIENT-PORTAL-FRONTEND-H, CLIENT-PORTAL-FRONTEND-1J
    if (localStorage.getItem('userType') === 'client') {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userType');
      localStorage.removeItem('authMethod');
      localStorage.removeItem('userInfo');
    }

    // Check if already authenticated using clientTokenManager (sessionStorage)
    // This aligns with __root.tsx which also uses clientTokenManager for client auth
    const token = clientTokenManager.getStoredToken();
    const userType = clientTokenManager.getUserType();

    if (token && userType === 'client') {
      throw redirect({
        to: '/',
      });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [justAuthenticated, setJustAuthenticated] = useState(false);
  const { data: authState } = useAuthState();
  const { data: availableClients = [], isLoading: isLoadingClients } = useAvailableClients();

  // After successful login, check if we need to redirect to client selection or contact info
  useEffect(() => {
    if (justAuthenticated && !isLoadingClients && authState?.isAuthenticated) {
      // Check if user has email or phone number, or if it's their first login
      const email = authState?.user?.email;
      const phoneNumber = authState?.user?.phoneNumber;
      const hasEmail = typeof email === 'string' ? email.trim().length > 0 : Boolean(email);
      const hasPhoneNumber = typeof phoneNumber === 'string' ? phoneNumber.trim().length > 0 : Boolean(phoneNumber);
      const isFirstLogin = sessionStorage.getItem('isFirstLogin') === 'true';

      // A phone number is normally required (tax e-sign / SMS verification). Skip that
      // requirement only when NONE of the accessible clients has the Tax service line —
      // audit-only work doesn't use SMS/e-sign. A client that has both Tax and Audit still
      // requires a phone number, so we gate on the absence of Tax rather than presence of Audit.
      const anyClientHasTax = availableClients.some((client) => client.serviceLines?.includes(ServiceLine.Tax));
      const phoneRequired = anyClientHasTax;

      log.debug('Login check:', { hasEmail, hasPhoneNumber, isFirstLogin, phoneRequired, user: authState?.user });

      // Redirect to contact info if missing email, missing a required phone, or first login
      if (!hasEmail || (!hasPhoneNumber && phoneRequired) || isFirstLogin) {
        // Clear the first login flag after checking
        sessionStorage.removeItem('isFirstLogin');
        navigate({ to: '/contact-info' });
        return;
      }

      const storedClientId = sessionStorage.getItem('selectedClientId');

      // Validate that the stored client ID is still in the available clients list
      const validStoredClientId = storedClientId && availableClients.some((client) => client.id === storedClientId);

      // If user has multiple clients and no valid client is selected, redirect to client selection
      if (availableClients.length > 1 && !validStoredClientId) {
        navigate({ to: '/client-select' });
      } else if (availableClients.length === 1 || validStoredClientId) {
        // Single client or valid client already selected - go to home
        navigate({ to: '/' });
      }
    }
  }, [justAuthenticated, isLoadingClients, authState, availableClients, navigate]);

  const handleSuccess = () => {
    // Set flag to trigger redirect logic after authentication
    setJustAuthenticated(true);
  };

  // Show loading state while checking client availability after authentication
  if (justAuthenticated && isLoadingClients) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <PasswordlessAuthFlow onSuccess={handleSuccess} />;
}
